import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Info, X } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { ErrorBox } from "@/components/portal/LoadingError"
import { DatenstandHinweis } from "@/components/portal/DatenstandHinweis"
import { toast } from "@/components/portal/Toast"
import { PlanerSolSchattenBlock } from "@/components/portal/PlanerSolSchattenBlock"

/**
 * „Rückkopplung" — die Schleife, in der der Assistent besser wird (#64).
 *
 * Paul am 10.09.2026: „es muss ja auch dauerhaft eine Rückkopplung geben,
 * nicht nur einmalig." Und am 11.09.: Dashboard für den Menschen — am Ende,
 * wenn die Daten da sind.
 *
 * Was hier steht, kommt aus vier Stufen, alle automatisch:
 *   1. Ausgang: wie jeder Plan endete (ausgeführt, verworfen, liegen geblieben …)
 *   2. Nachschau: was stattdessen geschah — Portal, Telefon, HERO
 *   3. Deutung: warum, als Hypothese mit Konfidenz
 *   4. Verdichtung: einmal je Woche ein Wochenbericht und Regel-Entwürfe
 *
 * 🔑 Der Mensch sitzt an der REGEL, nicht am Fall: Entwürfe übernehmen oder
 * verwerfen. Übernehmen schreibt in die aktiven Regeln des Kontext-Centers,
 * nie in den Prompt. Danach misst das System die Wirkung.
 */

type Regel = {
  id: number; tenant_slug: string; grund: string; grund_text: string; befund: string | null
  regel_text: string; belege: number[]; anzahl: number; status: string
  erstellt_at: string; entschieden_at: string | null; entschieden_von: string | null; uebernommen_at: string | null
  wirkung: { vorher?: { quote: number | null; gesamt: number }; nachher?: { quote: number | null; gesamt: number }
             besser?: boolean | null; tage_seit_uebernahme?: number; verworfen_grund?: string | null } | null
}
type Verdichtung = { id: number; tenant_slug: string; erstellt_at: string; zeitraum_tage: number; zusammenfassung: string; modell: string | null; anzahl_vorschlaege: number }
type Kennzahlen = {
  ausgaenge?: { je_ausgang: Record<string, number>; plaene_gesamt: number }
  nachschau?: { je_typ: { typ: string; anzahl: number; mails: number }[]; plaene_nicht_wie_vorgeschlagen: number; mit_nachschau: number; ohne_spur: number }
  deutung?: { je_grund: { grund: string; text: string; anzahl: number; konfidenz: number; daumen_hoch: number; daumen_runter: number; mit_regel: number }[]; gedeutet: number; unklar: number; tageslimit: number }
  gegenprobe?: { geprueft: number; uebereinstimmend: number; quote: number | null; wochenlimit: number; je_abweichung: { haiku_text: string; sonnet_text: string; anzahl: number }[] }
  no_touch_quote?: number | null; no_touch_basis?: number
  /** Das Gesamtbild: alle Pläne ohne „Keine Aktion" — genutzt, anders erledigt, liegen geblieben. */
  gesamtbild?: { wie_vorgeschlagen: number; mit_hinweis_korrigiert?: number; mit_aenderung: number; zur_kenntnis?: number; inhaber_direkt?: number; anders_erledigt: number; nichts_noetig: number
                 liegen_geblieben: number; offen: number; gesamt: number; nutzquote: number | null; trefferquote: number | null }
}

const AUSGANG: Record<string, string> = {
  ausgefuehrt: "ausgeführt", teilweise: "teilweise", beantwortet: "beantwortet", weitergeleitet: "weitergeleitet",
  verworfen: "verworfen", erledigt: "von Hand erledigt", abgelaufen: "liegen geblieben",
  anders_erledigt: "anders erledigt (von selbst geschlossen)",
  nichts_noetig: "nichts nötig (von selbst geschlossen)",
}
const SPUR: Record<string, string> = {
  antwort_gesendet: "eigene Mail an den Kunden", kunde_schrieb_erneut: "Kunde schrieb erneut", rueckruf: "Rückruf",
  rueckruf_versuch: "Rückruf versucht, kein Gespräch",
  anruf_kunde: "Kunde rief an", bot_anruf: "KI-Telefon", projekt_angelegt: "Vorgang in HERO angelegt",
  termin_angelegt: "Termin in HERO angelegt", termin_vorhanden: "Termin stand schon in HERO", aufgabe_angelegt: "Aufgabe angelegt", beleg_erstellt: "Beleg erstellt",
  email_status_change: "Mail-Status von Hand geändert", email_flag: "Mail markiert", event_create: "Termin im Portal angelegt",
  event_update: "Termin geändert", event_delete: "Termin gelöscht", contact_update: "Kontakt geändert", task_create: "Aufgabe im Portal angelegt",
  status_no_op: "Status unverändert bestätigt", email_forward: "weitergeleitet",
  termin_verschoben: "Termin im Portal verschoben", zurueck_auf_neu: "bewusst zurück auf „neu“", markierung_entfernt: "Markierung entfernt",
  email_move: "im Portal in einen Ordner verschoben",
}
const GESAMT: { key: keyof NonNullable<Kennzahlen["gesamtbild"]>; label: string; hinweis: string }[] = [
  { key: "wie_vorgeschlagen", label: "wie vorgeschlagen übernommen", hinweis: "ausgeführt oder gesendet, nichts geändert" },
  { key: "mit_hinweis_korrigiert", label: "mit Hinweis korrigiert", hinweis: "per Hinweis neu geplant, der neue Entwurf ging unverändert raus – zählt als übernommen, nicht als Treffer" },
  { key: "mit_aenderung", label: "übernommen, aber korrigiert", hinweis: "Schritt abgewählt, ergänzt oder Text umgeschrieben" },
  { key: "zur_kenntnis", label: "zur Kenntnis genommen und geschlossen", hinweis: "Plan sagte „nur zur Kenntnis“, das Büro hat die Mail geschlossen – zählt als genutzt" },
  { key: "inhaber_direkt", label: "vom Inhaber selbst erledigt (zählt nicht mit)", hinweis: "Antwort aus Outlook oder vom Handy, das Büro hat nichts dazu angelegt" },
  { key: "anders_erledigt", label: "anders erledigt", hinweis: "nicht übernommen, aber Mail, Anruf oder HERO-Eintrag gefunden" },
  { key: "nichts_noetig", label: "nichts nötig", hinweis: "keine Spur, Deutung: kein Handlungsbedarf" },
  { key: "liegen_geblieben", label: "liegen geblieben", hinweis: "keine Spur, kein Grund erkennbar" },
  { key: "offen", label: "Nachschau läuft noch", hinweis: "Fenster von 14 Tagen noch nicht zu" },
]
const STATUS: Record<string, { label: string; farbe: string }> = {
  entwurf: { label: "Entwurf", farbe: "#b45309" }, uebernommen: { label: "übernommen", farbe: "#047857" },
  verworfen: { label: "verworfen", farbe: "#6b7280" }, wirkungslos: { label: "wirkungslos", farbe: "#b91c1c" },
  entfernt: { label: "aus dem Kontext-Center entfernt", farbe: "#6b7280" },
}

const datum = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—"

async function post(url: string, body?: unknown) {
  const r = await fetch(url, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
                              body: body === undefined ? undefined : JSON.stringify(body) })
  const text = await r.text()
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${text.slice(0, 160)}`)
  return text ? JSON.parse(text) : {}
}

function Wirkung({ w }: { w: Regel["wirkung"] }) {
  if (!w || !w.vorher) return <span className="text-muted-foreground">Wirkung: noch nicht geprüft (frühestens nach 7 Tagen)</span>
  const v = w.vorher.quote, n = w.nachher?.quote
  const text = `Anteil dieses Grunds vorher ${v ?? "—"} % (${w.vorher.gesamt} Fälle) → nachher ${n ?? "—"} % (${w.nachher?.gesamt ?? 0} Fälle)`
  if (w.besser === true) return <span style={{ color: "#047857" }}>Wirkt: {text}</span>
  if (w.besser === false) return <span style={{ color: "#b91c1c" }}>Keine Besserung: {text}</span>
  return <span className="text-muted-foreground">Noch zu wenig Fälle danach: {text}</span>
}

export default function RueckkopplungPage() {
  const [tage, setTage] = useState(30)
  const [tenant, setTenant] = useState("")
  const qc = useQueryClient()

  const tenantList = useQuery<string[]>({
    queryKey: ["rueckkopplung-tenants"],
    queryFn: async () => {
      const r = await fetch("/api/v1/tenants?include_test=true", { credentials: "include" })
      if (!r.ok) return []
      const d = await r.json()
      return Array.isArray(d.items) ? d.items.map((t: { slug: string }) => t.slug) : []
    },
    staleTime: 1000 * 60 * 5,
  })
  // #101: `daten_ab` = ältester Eintrag, aus dem die Zahlen zählen (je Mandant).
  const kenn = useQuery<{ kennzahlen: Kennzahlen; daten_ab?: string | null }>({
    queryKey: ["rueckkopplung-kennzahlen", tage, tenant],
    queryFn: async () => {
      const p = new URLSearchParams({ tage: String(tage) }); if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/vorschlagsqualitaet?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const regeln = useQuery<{ regeln: Regel[] }>({
    queryKey: ["rueckkopplung-regeln", tenant],
    queryFn: async () => {
      const p = new URLSearchParams(); if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/rueckkopplung/regeln?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })
  const berichte = useQuery<{ verdichtungen: Verdichtung[] }>({
    queryKey: ["rueckkopplung-verdichtungen", tenant],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: "6" }); if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/rueckkopplung/verdichtungen?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json()
    },
  })

  const neuLaden = () => {
    qc.invalidateQueries({ queryKey: ["rueckkopplung-regeln"] })
    qc.invalidateQueries({ queryKey: ["rueckkopplung-verdichtungen"] })
    qc.invalidateQueries({ queryKey: ["rueckkopplung-kennzahlen"] })
  }
  const uebernehmen = useMutation({
    mutationFn: (id: number) => post(`/api/v1/rueckkopplung/regeln/${id}/uebernehmen`),
    onSuccess: (d: { hinweis?: string | null; regeln_gesamt?: number }) => {
      toast(d.hinweis ? `Übernommen. ${d.hinweis}` : `✓ Regel übernommen — steht jetzt im Kontext-Center (${d.regeln_gesamt} Regeln)`)
      neuLaden()
    },
    onError: (e) => toast("Übernehmen fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const verwerfen = useMutation({
    mutationFn: (id: number) => post(`/api/v1/rueckkopplung/regeln/${id}/verwerfen`, { grund: null }),
    onSuccess: () => { toast("Entwurf verworfen"); neuLaden() },
    onError: (e) => toast("Verwerfen fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })
  const verdichten = useMutation({
    mutationFn: () => post(`/api/v1/rueckkopplung/verdichten?tenant=${encodeURIComponent(tenant)}`),
    onSuccess: (d: { vorschlaege?: unknown[]; modell?: string | null }) => {
      toast(d.modell ? `Wochenbericht erstellt, ${(d.vorschlaege || []).length} Vorschlag/Vorschläge` : "Zu wenig Daten für einen Wochenbericht — kein Modellaufruf")
      neuLaden()
    },
    onError: (e) => toast("Verdichten fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  if (kenn.error) return <div className="p-10"><ErrorBox error={kenn.error} /></div>

  const k = kenn.data?.kennzahlen || {}
  const alleRegeln = regeln.data?.regeln || []
  const entwuerfe = alleRegeln.filter((r) => r.status === "entwurf")
  const uebernommene = alleRegeln.filter((r) => r.status === "uebernommen" || r.status === "wirkungslos" || r.status === "entfernt")
  const verworfene = alleRegeln.filter((r) => r.status === "verworfen")
  const tenants = tenantList.data || []
  const bericht = (berichte.data?.verdichtungen || [])[0]

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      <PageHeader title="Rückkopplung" onRefresh={neuLaden} refreshing={kenn.isFetching || regeln.isFetching} />
      <p className="text-[13px] text-muted-foreground mb-4 max-w-[72ch]">
        Wie die Pläne des Assistenten endeten, was das Büro stattdessen tat, warum — und welche
        Regeln daraus folgen. Alles automatisch gesammelt; entschieden wird hier nur über Regeln.
      </p>

      <div className="flex gap-4 items-center text-[13px] mb-5 flex-wrap">
        <label className="inline-flex items-center gap-1.5">
          Zeitraum
          <select className="border rounded px-2 py-1 bg-transparent" value={tage} onChange={(e) => setTage(Number(e.target.value))}>
            {[14, 30, 90].map((t) => <option key={t} value={t}>{t} Tage</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-1.5">
          Mandant
          <select className="border rounded px-2 py-1 bg-transparent" value={tenant} onChange={(e) => setTenant(e.target.value)}>
            <option value="">alle</option>
            {tenants.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        {tenant && (
          <button type="button" className="pp-btn pp-btn-sm" disabled={verdichten.isPending}
                  onClick={() => verdichten.mutate()} title="Ein Modellaufruf. Läuft sonst automatisch einmal je Woche.">
            {verdichten.isPending ? "Verdichte…" : "Wochenbericht jetzt erstellen"}
          </button>
        )}
      </div>

      <DatenstandHinweis datenAb={kenn.data?.daten_ab} tage={tage} />

      {/* ── Regel-Entwürfe: hier sitzt der Mensch ─────────────────────── */}
      <h2 className="text-[14px] font-semibold mb-2">Regel-Entwürfe ({entwuerfe.length})</h2>
      {entwuerfe.length === 0 ? (
        <div className="pp-card p-4 mb-5 text-[13px] text-muted-foreground">
          Keine offenen Entwürfe. Sie entstehen im Wochenbericht, sobald ein Grund mindestens dreimal vorkam.
        </div>
      ) : (
        <div className="pp-card divide-y mb-5">
          {entwuerfe.map((r) => (
            <div key={r.id} className="px-4 py-3 text-[13px]" data-testid={`regel-${r.id}`}>
              <div className="font-medium">{r.regel_text}</div>
              <div className="text-[12.5px] text-muted-foreground mt-0.5">
                {r.grund_text}{r.befund ? ` — ${r.befund}` : ""}{r.anzahl ? ` · ${r.anzahl} Belege` : ""}
                {!tenant && r.tenant_slug ? ` · ${r.tenant_slug}` : ""} · {datum(r.erstellt_at)}
              </div>
              <div className="flex gap-2 mt-2">
                <button type="button" className="pp-btn pp-btn-sm" disabled={uebernehmen.isPending}
                        onClick={() => uebernehmen.mutate(r.id)}>
                  <Check className="w-3.5 h-3.5 inline mr-1" />Übernehmen (ins Kontext-Center)
                </button>
                <button type="button" className="pp-btn pp-btn-sm" disabled={verwerfen.isPending}
                        onClick={() => verwerfen.mutate(r.id)}>
                  <X className="w-3.5 h-3.5 inline mr-1" />Verwerfen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Wochenbericht ────────────────────────────────────────────── */}
      <h2 className="text-[14px] font-semibold mb-2">Letzter Wochenbericht</h2>
      <div className="pp-card p-4 mb-5 text-[13px]">
        {bericht ? (
          <>
            <div className="text-[12px] text-muted-foreground mb-1">
              {datum(bericht.erstellt_at)} · letzte {bericht.zeitraum_tage} Tage · {bericht.tenant_slug}
              {bericht.modell ? "" : " · ohne Modell"}
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{bericht.zusammenfassung}</div>
          </>
        ) : (
          <span className="text-muted-foreground">Noch kein Wochenbericht. Er entsteht automatisch, sobald genug gedeutete Fälle da sind.</span>
        )}
      </div>

      {/* ── Übernommene Regeln und ihre Wirkung ──────────────────────── */}
      <h2 className="text-[14px] font-semibold mb-2">Übernommene Regeln ({uebernommene.length})</h2>
      {uebernommene.length === 0 ? (
        <div className="pp-card p-4 mb-5 text-[13px] text-muted-foreground">Noch keine Regel übernommen.</div>
      ) : (
        <div className="pp-card divide-y mb-5">
          {uebernommene.map((r) => (
            <div key={r.id} className="px-4 py-3 text-[13px]">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium">{r.regel_text}</span>
                <span className="text-[11px] px-1.5 rounded" style={{ color: STATUS[r.status]?.farbe, border: `1px solid ${STATUS[r.status]?.farbe}` }}>
                  {STATUS[r.status]?.label ?? r.status}
                </span>
              </div>
              <div className="text-[12.5px] text-muted-foreground mt-0.5">
                {r.grund_text} · übernommen {datum(r.uebernommen_at)}{r.entschieden_von ? ` von ${r.entschieden_von}` : ""}
                {!tenant && r.tenant_slug ? ` · ${r.tenant_slug}` : ""}
              </div>
              <div className="text-[12.5px] mt-1"><Wirkung w={r.wirkung} /></div>
            </div>
          ))}
        </div>
      )}

      {/* ── Gesamtbild: Ausgang, Nachschau und Deutung in einem ──────── */}
      <h2 className="text-[14px] font-semibold mb-2">Das Gesamtbild (letzte {tage} Tage, ohne Pläne „Keine Aktion“)</h2>
      <div className="pp-card p-4 mb-5 text-[12.5px]" data-testid="gesamtbild">
        {(k.gesamtbild?.gesamt ?? 0) === 0 ? <span className="text-muted-foreground">noch keine Pläne mit Ausgang</span> : (
          <>
            <div className="flex items-baseline gap-4 flex-wrap mb-2">
              <span><span className="text-[22px] font-semibold">{k.gesamtbild!.nutzquote ?? "—"} %</span> <span className="text-muted-foreground">der {k.gesamtbild!.gesamt} Pläne wurden genutzt</span></span>
              <span><span className="text-[22px] font-semibold">{k.gesamtbild!.trefferquote ?? "—"} %</span> <span className="text-muted-foreground">davon ohne jede Korrektur</span></span>
            </div>
            <ul className="grid md:grid-cols-3 gap-x-4 gap-y-1">
              {GESAMT.map((g) => (
                <li key={g.key} title={g.hinweis}><strong>{k.gesamtbild![g.key] ?? 0}</strong> {g.label}</li>
              ))}
            </ul>
            <div className="text-muted-foreground mt-2">
              „Anders erledigt“ und „nichts nötig“ sind kein Fehler des Assistenten. „Liegen geblieben“ ist ein Kapazitätsthema im Büro.
              Für die Vorschlagsqualität zählt die zweite Zahl.
            </div>
          </>
        )}
      </div>

      {/* ── Zahlen: Ausgänge, Nachschau, Deutungen ───────────────────── */}
      <h2 className="text-[14px] font-semibold mb-2">Die Zahlen dahinter (letzte {tage} Tage)</h2>
      <div className="grid md:grid-cols-3 gap-3 mb-5">
        <div className="pp-card p-3 text-[12.5px]">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Wie die Pläne endeten</div>
          {(k.ausgaenge?.plaene_gesamt ?? 0) === 0 ? <span className="text-muted-foreground">noch keine</span> : (
            <ul>
              {Object.entries(k.ausgaenge?.je_ausgang || {}).filter(([, n]) => n > 0).map(([a, n]) => (
                <li key={a}><strong>{AUSGANG[a] ?? a}</strong>: {n}</li>
              ))}
            </ul>
          )}
          {k.no_touch_basis ? <div className="mt-1 text-muted-foreground">KI-Quote: {k.no_touch_quote ?? "—"} % von {k.no_touch_basis}</div> : null}
        </div>
        <div className="pp-card p-3 text-[12.5px]">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Was stattdessen geschah</div>
          {(k.nachschau?.je_typ || []).length === 0 ? <span className="text-muted-foreground">noch keine Spuren</span> : (
            <ul>
              {(k.nachschau?.je_typ || []).map((t) => <li key={t.typ}><strong>{SPUR[t.typ] ?? t.typ}</strong>: {t.anzahl}× in {t.mails} Mails</li>)}
            </ul>
          )}
          {k.nachschau ? <div className="mt-1 text-muted-foreground">{k.nachschau.ohne_spur} von {k.nachschau.plaene_nicht_wie_vorgeschlagen} Plänen ohne jede Spur</div> : null}
        </div>
        <div className="pp-card p-3 text-[12.5px]">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Warum (Deutung)</div>
          {(k.deutung?.je_grund || []).length === 0 ? <span className="text-muted-foreground">noch keine Deutung</span> : (
            <ul>
              {(k.deutung?.je_grund || []).map((g) => (
                <li key={g.grund} title={g.text}>
                  <strong>{g.text}</strong>: {g.anzahl} (Konfidenz {g.konfidenz})
                  {g.daumen_hoch + g.daumen_runter > 0 ? ` · 👍 ${g.daumen_hoch} 👎 ${g.daumen_runter}` : ""}
                </li>
              ))}
            </ul>
          )}
          {k.deutung ? <div className="mt-1 text-muted-foreground">{k.deutung.gedeutet} gedeutet, {k.deutung.unklar} unklar · Limit {k.deutung.tageslimit}/Tag</div> : null}
          {k.gegenprobe && k.gegenprobe.geprueft > 0 ? (
            <div
              className="mt-1 text-muted-foreground"
              title={`Jede Woche werden ${k.gegenprobe.wochenlimit} Deutungen zufällig ausgewählt und vom stärkeren Modell noch einmal beurteilt — mit derselben Frage. Stimmt der Grund überein, trägt die Deutung.`}
            >
              Gegenprobe: {k.gegenprobe.uebereinstimmend} von {k.gegenprobe.geprueft} Deutungen vom stärkeren Modell bestätigt
              {k.gegenprobe.quote != null ? ` (${k.gegenprobe.quote} %)` : ""}
              {k.gegenprobe.je_abweichung.length > 0
                ? ` · abweichend: ${k.gegenprobe.je_abweichung.slice(0, 3).map((a) => `${a.haiku_text} → ${a.sonnet_text} (${a.anzahl}×)`).join("; ")}`
                : ""}
            </div>
          ) : null}
        </div>
      </div>

      {verworfene.length > 0 && (
        <details className="text-[12.5px] text-muted-foreground mb-5">
          <summary className="cursor-pointer">Verworfene Entwürfe ({verworfene.length})</summary>
          <ul className="mt-1 ml-4 list-disc">
            {verworfene.map((r) => <li key={r.id}>{r.regel_text}{r.wirkung?.verworfen_grund ? ` — ${r.wirkung.verworfen_grund}` : ""}</li>)}
          </ul>
        </details>
      )}

      {/* Baustein 14: Sol-Planer im Schatten — nur sichtbar, wenn an oder Fälle da */}
      <PlanerSolSchattenBlock tage={tage} tenant={tenant} />

      <div className="flex gap-2 items-start rounded-md border px-3 py-2 text-[12.5px] text-muted-foreground">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          Übernommene Regeln stehen im Kontext-Center unter „Aktive Regeln“ und wirken beim nächsten Plan.
          Der Planer liest die ersten 15 Regeln — bei mehr die Liste dort ausmisten. Nach sieben Tagen misst
          das System, ob der Grund seltener wurde; bleibt er nach 14 Tagen gleich, gilt die Regel als wirkungslos.
        </div>
      </div>
    </div>
  )
}
